import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from "react-hook-form";
import CompanyAddressForm from "../CompanyAddressForm";
import DynamicSelect from "../../dynamic_select/DynamicSelect";

const CompanyDetails = ({ 
    selectedCompany, 
    fetchCompany,
    statusOptions,
}) => {

    const {
        register,
        watch,
        setValue,
        reset,
        formState: { isDirty },
    } = useFormContext();

    useEffect(() => {
        register('name');
        register('address');
        register('status');
        register('brand_primary_color');
        register('brand_secondary_color');
        register('logo_url');
        register('company_latitude');
        register('company_longitude');
    }, []);

    const [name, address, company_latitude, company_longitude, status, brand_primary_color, brand_secondary_color, logo_url ] = watch([
        'name',
        'address',
        'company_latitude',
        'company_longitude',
        'status',
        'brand_primary_color',
        'brand_secondary_color',
        'logo_url'
    ]);

    const [isLoading, setIsLoading] = useState(false);

    const updateCompany = async (data) => {
        setIsLoading(true)
        try {
            const data = {
                name,
                address,
                company_latitude,
                company_longitude,
                status,
                brand_primary_color,
                brand_secondary_color,
                logo_url
            }

            await axiosInstance(`/companies/${selectedCompany.id}.json`, {
                method: 'PUT',
                data: { company: data },
            });

            await fetchCompany();

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Company can't be updated.", error)
        }
    }

    // Revert unsaved changes
    const handleRevertChanges = async () => {
        reset();
    };
    
    return (
        <div className="w-full p-4 flex flex-col">

            {/* Basic Details section */}
            <div className="basic_details flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Basic Details</h3>
                        <p className="text-sm text-gray-500 break-words">Basic details about the company</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col space-y-4">

                    <div className="w-full flex flex-col">
                        <label className="label font-medium text-gray-900">Company Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setValue('name', e.target.value, { shouldDirty: true })}
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                border: '1px solid #ccc', 
                                borderRadius: '4px' 
                            }}
                            className="input border text-sm py-1 rounded-lg w-full md:w-3/4 lg:w-5/6 border-gray-400 text-gray-900 px-2"
                        />
                    </div>
                    <div className="w-full flex flex-col">
                        <label className="label font-medium text-gray-900">Company Primary Address</label>
                        <CompanyAddressForm
                            address={address}
                            company_latitude={company_latitude}
                            company_longitude={company_longitude}
                        />
                    </div>
                    <div className="w-full flex flex-col" style={{ marginTop: '2px' }}>
                        <label className="label font-medium text-gray-900">Company Status</label>
                        <DynamicSelect
                            props={{
                                onChange: (e) => {
                                    if (e?.target?.value?.value)
                                        setValue('status', e.target.value.value, { shouldDirty: true })
                                },
                                options: statusOptions,
                                placeholder: "Select Status",
                                value: status,
                            }}
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Branding Details section */}
            <div className="branding flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Branding Details</h3>
                        <p className="text-sm text-gray-500">Description of the branding</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">

                    <div className="w-full flex flex-wrap">

                        <div className="w-full md:w-1/2 flex flex-col mb-3 pr-1">
                            <label htmlFor="brand_primary_colour" className="label">Brand Primary Colour</label>
                            <input 
                                type="color"
                                autoFocus
                                value={brand_primary_color}
                                onChange={(e) => setValue('brand_primary_color', e.target.value,{ shouldDirty: true })}
                                className="block w-full h-12 border rounded"
                            />
                        </div>

                        <div className="w-full md:w-1/2 flex flex-col mb-3 pl-1">
                            <label htmlFor="brand_secondary_colour" className="label">Brand Secondary Colour</label>
                            <input 
                                type="color"
                                value={brand_secondary_color}
                                onChange={(e) => setValue('brand_secondary_color', e.target.value, { shouldDirty: true })}
                                autoFocus 
                                className="block w-full h-12 border rounded"
                            />
                        </div>
                    </div>

                    <div className="w-full flex flex-col mb-3 mt-3">
                        <label className="label font-medium text-gray-900">Logo URL</label>
                        <input
                            type="text"
                            value={logo_url}
                            onChange={(e) => setValue('logo_url', e.target.value, { shouldDirty: true })}
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                border: '1px solid #ccc', 
                                borderRadius: '4px' 
                            }}
                            className="input border text-sm py-1 rounded-lg w-full md:w-3/4 lg:w-5/6 border-gray-400 text-gray-900 px-2"
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={updateCompany}
                        disabled={isDirty == false || company_latitude == null || company_longitude == null}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer bg-gray-500 hover:bg-gray-600`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={handleRevertChanges}
                        disabled={isDirty == false}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompanyDetails;